import React, { useState, useEffect, useMemo } from 'react';
import api from '../../config/api';
import { FaEye, FaCheck, FaTimes, FaTrash, FaFileExcel, FaEdit } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { formatBirthDateDisplayUtc } from '../../utils/dateInput';
import { formatMatchDate, formatMatchDateOnly } from '../../utils/matchDateTime';
import { formatPhoneDisplay } from '../../utils/formatPhoneDisplay';
import { getZoneFromSector } from '../../utils/stadiumSectorGroups';
import { getMembershipCodesForSeason } from '../../utils/membershipCodes';
import { requiresTravelInformationForm } from '../../utils/europeanAwayTravel';
import {
  computeOrderTicketPricingFromOrder,
  getOrderTotalPriceFromOrder,
  getOrderUnitPriceFromOrder,
} from '../../utils/ticketMinorPricing';
import './AdminPages.css';

const TRANSPORT_MODE_LABELS = {
  car: 'Voiture',
  train: 'Train',
  plane: 'Avion',
  bus: 'Bus',
};

function formatAdminDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('fr-CH');
  } catch {
    return '—';
  }
}

function formatTravelLegDate(value) {
  if (!value) return '—';
  return formatMatchDateOnly(value, 'fr-FR') || '—';
}

function orderHasTravelInformation(order) {
  return Boolean(
    order?.travelInformation?.waiverAccepted
    && Array.isArray(order.travelInformation?.perAttendee)
    && order.travelInformation.perAttendee.length > 0,
  );
}

function renderTravelLegDetail(leg) {
  if (!leg) {
    return <p className="admin-travel-empty">—</p>;
  }

  const mode = leg.transportMode;

  return (
    <div className="detail-grid admin-travel-leg-grid">
      <div className="detail-item">
        <label>Pays de départ:</label>
        <span>{leg.departureCountry || '—'}</span>
      </div>
      <div className="detail-item">
        <label>Ville de départ:</label>
        <span>{leg.departureCity || '—'}</span>
      </div>
      <div className="detail-item">
        <label>Date:</label>
        <span>{formatTravelLegDate(leg.date)}</span>
      </div>
      <div className="detail-item">
        <label>Heure d&apos;arrivée:</label>
        <span>{leg.arrivalTime || '—'}</span>
      </div>
      <div className="detail-item">
        <label>Mode de transport:</label>
        <span>{TRANSPORT_MODE_LABELS[mode] || mode || '—'}</span>
      </div>
      {mode === 'car' && (
        <div className="detail-item">
          <label>Plaque d&apos;immatriculation:</label>
          <span>{leg.licensePlate || '—'}</span>
        </div>
      )}
      {mode === 'train' && (
        <>
          <div className="detail-item">
            <label>Numéro de train:</label>
            <span>{leg.trainNumber || '—'}</span>
          </div>
          <div className="detail-item">
            <label>Gare:</label>
            <span>{leg.trainStation || '—'}</span>
          </div>
          <div className="detail-item">
            <label>Ville de la gare:</label>
            <span>{leg.trainStationCity || '—'}</span>
          </div>
        </>
      )}
      {mode === 'plane' && (
        <>
          <div className="detail-item">
            <label>Numéro de vol:</label>
            <span>{leg.flightNumber || '—'}</span>
          </div>
          <div className="detail-item">
            <label>Aéroport:</label>
            <span>{leg.airport || '—'}</span>
          </div>
        </>
      )}
      {mode === 'bus' && (
        <div className="detail-item">
          <label>Lieu de départ (bus):</label>
          <span>{leg.busDeparturePlace || '—'}</span>
        </div>
      )}
    </div>
  );
}

function renderOrderTravelInformationSection(order) {
  const ticket = order?.ticket;
  const showSection = requiresTravelInformationForm(ticket) || orderHasTravelInformation(order);
  if (!showSection) return null;

  const travel = order.travelInformation;
  const filled = orderHasTravelInformation(order);
  const rows = filled
    ? [...travel.perAttendee].sort((a, b) => (a.attendeeIndex ?? 0) - (b.attendeeIndex ?? 0))
    : [];

  return (
    <section className="detail-section">
      <h3>Informations de voyage</h3>
      {!filled ? (
        <p className="admin-travel-empty">Non renseignées pour cette commande.</p>
      ) : (
        <>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Décharge acceptée:</label>
              <span>Oui</span>
            </div>
            <div className="detail-item">
              <label>Date d&apos;acceptation:</label>
              <span>{formatAdminDateTime(travel.waiverAcceptedAt)}</span>
            </div>
          </div>
          {rows.map((row, index) => {
            const name = [row.firstName, row.lastName].filter(Boolean).join(' ') || `Personne ${index + 1}`;
            return (
              <div key={`travel-${row.attendeeIndex ?? index}`} className="admin-travel-person-block">
                <h4>{name}</h4>
                <p className="admin-travel-leg-title">Voyage aller</p>
                {renderTravelLegDetail(row.outbound)}
                <p className="admin-travel-leg-title">Voyage retour</p>
                {renderTravelLegDetail(row.returnLeg)}
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}

const statusLabelFr = (status) =>
  ({ pending: 'En attente', confirmed: 'Confirmée', cancelled: 'Annulée' }[status] || status || '');

function formatExcelDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('fr-CH');
  } catch {
    return '';
  }
}

const EXCEL_HEADERS = [
  'ID commande',
  'Date commande',
  'Statut',
  'Quantité',
  'Zone',
  'Secteur',
  'Notes',
  'Match',
  'Stade',
  'Date du match',
  'Nom',
  'Prénom',
  'Numéro Jcard',
  'No E-card',
  'Date de naissance',
  'Email',
  'Téléphone',
  'Codice fiscale',
];

function excelLegColumnHeaders(legLabel) {
  return [
    `${legLabel} — Pays de départ`,
    `${legLabel} — Ville de départ`,
    `${legLabel} — Date`,
    `${legLabel} — Heure d'arrivée`,
    `${legLabel} — Mode de transport`,
    `${legLabel} — Plaque d'immatriculation`,
    `${legLabel} — Numéro de train`,
    `${legLabel} — Gare`,
    `${legLabel} — Ville de la gare`,
    `${legLabel} — Numéro de vol`,
    `${legLabel} — Aéroport`,
    `${legLabel} — Lieu de départ (bus)`,
  ];
}

const EXCEL_TRAVEL_HEADERS = [
  'Voyage — Décharge acceptée',
  'Voyage — Date acceptation décharge',
  ...excelLegColumnHeaders('Aller'),
  ...excelLegColumnHeaders('Retour'),
];

const EXCEL_TRAVEL_COLUMN_COUNT = EXCEL_TRAVEL_HEADERS.length;
const EXCEL_TRAVEL_LEG_COLUMN_COUNT = excelLegColumnHeaders('Aller').length;

function formatTravelLegDateForExcel(value) {
  if (!value) return '';
  return formatMatchDateOnly(value, 'fr-FR') || '';
}

function travelLegToExcelCells(leg) {
  if (!leg) {
    return Array(EXCEL_TRAVEL_LEG_COLUMN_COUNT).fill('');
  }
  const mode = leg.transportMode;
  return [
    leg.departureCountry || '',
    leg.departureCity || '',
    formatTravelLegDateForExcel(leg.date),
    leg.arrivalTime || '',
    TRANSPORT_MODE_LABELS[mode] || mode || '',
    mode === 'car' ? leg.licensePlate || '' : '',
    mode === 'train' ? leg.trainNumber || '' : '',
    mode === 'train' ? leg.trainStation || '' : '',
    mode === 'train' ? leg.trainStationCity || '' : '',
    mode === 'plane' ? leg.flightNumber || '' : '',
    mode === 'plane' ? leg.airport || '' : '',
    mode === 'bus' ? leg.busDeparturePlace || '' : '',
  ];
}

function getTravelRowForAttendee(order, attendeeIndex) {
  if (!orderHasTravelInformation(order)) return null;
  const rows = order.travelInformation.perAttendee;
  return rows.find((row) => row.attendeeIndex === attendeeIndex) || null;
}

function travelExcelCellsForAttendee(order, attendeeIndex, { isCompanionRow = false } = {}) {
  const filled = orderHasTravelInformation(order);
  const travelRow = getTravelRowForAttendee(order, attendeeIndex);
  const waiverCells = isCompanionRow
    ? ['', '']
    : [
        filled ? 'Oui' : '',
        filled ? formatAdminDateTime(order.travelInformation.waiverAcceptedAt) : '',
      ];

  if (!travelRow) {
    return [...waiverCells, ...Array(EXCEL_TRAVEL_LEG_COLUMN_COUNT * 2).fill('')];
  }

  return [
    ...waiverCells,
    ...travelLegToExcelCells(travelRow.outbound),
    ...travelLegToExcelCells(travelRow.returnLeg),
  ];
}

function emptyTravelExcelCells() {
  return Array(EXCEL_TRAVEL_COLUMN_COUNT).fill('');
}

function getOrderAttendees(order) {
  return Array.isArray(order.attendees) ? order.attendees : [];
}

function getPrimaryAttendee(order) {
  return getOrderAttendees(order).find((a) => a.role === 'primary') || null;
}

function getOrderEcardDisplay(order) {
  const fromAttendees = getOrderAttendees(order)
    .map((a) => a.ecardCode)
    .filter(Boolean);
  if (fromAttendees.length) return fromAttendees.join(' · ');
  return order.ecardCode || '—';
}

function getOrderPnrDisplay(order) {
  const primary = getPrimaryAttendee(order);
  if (primary?.pnrCode) return primary.pnrCode;
  return order.pnrCode || '—';
}

function renderOrderAttendeesDetail(order) {
  const attendees = getOrderAttendees(order);
  const hasPrimary = Boolean(getPrimaryAttendee(order));
  let companionCount = 0;

  return attendees.map((attendee, idx) => {
    const billetLabel =
      attendee.role === 'primary'
        ? 'Billet 1 — Titulaire'
        : `Billet ${(hasPrimary ? 1 : 0) + ++companionCount} — Accompagnant (uniquement membre actif du JOFC Oltre Frontiera)`;

    return (
      <React.Fragment key={`${attendee.role}-${idx}`}>
        <div className="detail-item full-width">
          <label>{billetLabel}</label>
          <span>{[attendee.firstName, attendee.lastName].filter(Boolean).join(' ') || '—'}</span>
        </div>
        {attendee.ecardCode && (
          <div className="detail-item">
            <label>e-card:</label>
            <span>{attendee.ecardCode}</span>
          </div>
        )}
        {attendee.pnrCode && (
          <div className="detail-item">
            <label>Code PNR:</label>
            <span>{attendee.pnrCode}</span>
          </div>
        )}
      </React.Fragment>
    );
  });
}

function formatExportPhone(phone) {
  if (phone == null || String(phone).trim() === '') return '';
  return formatPhoneDisplay(phone);
}

function juventusCardNumberFromUser(u) {
  return u?.juventusFanCard?.number || '';
}

function personToExcelCells({
  lastName,
  firstName,
  jcardNumber,
  ecardCode,
  dateOfBirth,
  email,
  phone,
  codiceFiscale,
}) {
  return [
    lastName || '',
    firstName || '',
    jcardNumber || '',
    ecardCode || '',
    dateOfBirth ? formatBirthDateDisplayUtc(dateOfBirth, 'fr-CH') : '',
    email || '',
    formatExportPhone(phone),
    codiceFiscale || '',
  ];
}

function userToPersonExcelCells(u, codesOverride = {}) {
  const membership = getMembershipCodesForSeason(u);
  return personToExcelCells({
    lastName: u.lastName,
    firstName: u.firstName,
    jcardNumber: juventusCardNumberFromUser(u),
    ecardCode: codesOverride.ecardCode || membership.ecardCode || '',
    dateOfBirth: u.dateOfBirth,
    email: u.email,
    phone: u.phone,
    codiceFiscale: u.codiceFiscale,
  });
}

function companionToPersonExcelCells(companion) {
  return personToExcelCells({
    lastName: companion.lastName,
    firstName: companion.firstName,
    jcardNumber: companion.juventusCard || '',
    ecardCode: companion.ecardCode || '',
    dateOfBirth: companion.dateOfBirth,
    email: companion.email,
    phone: companion.phone,
    codiceFiscale: '',
  });
}

function buildOrderExcelRow(order, personCells, { isCompanionRow = false, travelCells = [] } = {}) {
  const t = order.ticket || {};
  return [
    String(order._id || ''),
    isCompanionRow ? '' : formatExcelDate(order.orderDate),
    statusLabelFr(order.status),
    isCompanionRow ? '' : (order.quantity ?? ''),
    order.area || '',
    order.sector || '',
    order.notes || '',
    t.matchTitle || '',
    t.stadium || '',
    formatExcelDate(t.date),
    ...personCells,
    ...travelCells,
  ];
}

function orderToExcelRows(order, { includeTravel = false } = {}) {
  const u = order.user || {};
  const attendees = getOrderAttendees(order);
  const primary = getPrimaryAttendee(order);
  const primaryAttendeeIndex = attendees.findIndex((a) => a.role === 'primary');
  const resolvedPrimaryIndex = primaryAttendeeIndex >= 0 ? primaryAttendeeIndex : 0;
  const membership = getMembershipCodesForSeason(u);
  const primaryCodes = {
    ecardCode: primary?.ecardCode || order.ecardCode || membership.ecardCode || '',
  };
  const travelForOrder = includeTravel && orderHasTravelInformation(order);
  const primaryTravelCells = includeTravel
    ? (travelForOrder
        ? travelExcelCellsForAttendee(order, resolvedPrimaryIndex, { isCompanionRow: false })
        : emptyTravelExcelCells())
    : [];

  const rows = [
    buildOrderExcelRow(order, userToPersonExcelCells(u, primaryCodes), {
      travelCells: primaryTravelCells,
    }),
  ];

  attendees.forEach((attendee, attendeeIndex) => {
    if (attendee.role !== 'companion') return;
    const travelCells = includeTravel
      ? (travelForOrder
          ? travelExcelCellsForAttendee(order, attendeeIndex, { isCompanionRow: true })
          : emptyTravelExcelCells())
      : [];
    rows.push(
      buildOrderExcelRow(order, companionToPersonExcelCells(attendee), {
        isCompanionRow: true,
        travelCells,
      }),
    );
  });

  return rows;
}

function getOrderUnitPrice(ticket, order) {
  return getOrderUnitPriceFromOrder(ticket, order);
}

function formatOrderUnitPriceDisplay(ticket, order) {
  const pricing = computeOrderTicketPricingFromOrder(ticket, order);
  if (!pricing.ready) return 'N/A';
  if (pricing.adminFixed && pricing.uniformUnitPrice != null) {
    return `${pricing.uniformUnitPrice} €`;
  }
  if (pricing.mixed) {
    const parts = [];
    if (pricing.adultCount > 0 && pricing.adultUnit != null) {
      parts.push(`${pricing.adultUnit} € × ${pricing.adultCount} (adulte)`);
    }
    if (pricing.minorCount > 0 && pricing.minorUnit != null) {
      parts.push(`${pricing.minorUnit} € × ${pricing.minorCount} (mineur)`);
    }
    return parts.join(' · ') || 'N/A';
  }
  const unit = getOrderUnitPrice(ticket, order);
  return unit != null ? `${unit} €` : 'N/A';
}

function buildSectorOptions(ticket) {
  if (!ticket?.areaPricing) return [];

  const options = [];
  ticket.areaPricing.forEach((pricing) => {
    if (pricing.hasSectors && Array.isArray(pricing.sectors)) {
      pricing.sectors.forEach((sector) => {
        const zone = getZoneFromSector(sector.name) || pricing.area;
        options.push({
          value: sector.name,
          label: `${zone}${sector.name ? ` (${sector.name})` : ''} — ${sector.price} €`,
          area: pricing.area,
          price: sector.price,
        });
      });
    }
  });

  return options.sort((a, b) => {
    const numA = parseInt(a.value, 10);
    const numB = parseInt(b.value, 10);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
    return String(a.label).localeCompare(String(b.label), 'fr');
  });
}

function formatSectorDisplay(sector) {
  if (!sector) return 'N/A';
  const zone = getZoneFromSector(sector);
  return zone ? `${zone} (${sector})` : sector;
}

const AdminTicketOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMatch, setFilterMatch] = useState('all');
  const [seatingForm, setSeatingForm] = useState({ sector: '', unitPrice: '' });
  const [seatingSaving, setSeatingSaving] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (!selectedOrder) return;
    const unitPrice = getOrderUnitPrice(selectedOrder.ticket, selectedOrder);
    setSeatingForm({
      sector: selectedOrder.sector || '',
      unitPrice: unitPrice != null ? String(unitPrice) : '',
    });
  }, [selectedOrder]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ticket-orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.put(`/ticket-orders/${orderId}/status`, { status: newStatus });
      alert('Statut de la commande mis à jour');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande?')) return;
    
    try {
      await api.delete(`/ticket-orders/${orderId}`);
      alert('Commande supprimée avec succès');
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const sectorOptions = useMemo(
    () => (selectedOrder?.ticket ? buildSectorOptions(selectedOrder.ticket) : []),
    [selectedOrder],
  );

  const handleSeatingSectorChange = (sectorValue) => {
    const option = sectorOptions.find((o) => o.value === sectorValue);
    setSeatingForm({
      sector: sectorValue,
      unitPrice: option ? String(option.price) : seatingForm.unitPrice,
    });
  };

  const handleSeatingSave = async () => {
    if (!selectedOrder) return;

    if (!window.confirm(
      'Enregistrer les modifications et envoyer un e-mail au membre pour l\'informer du changement de secteur/prix ?',
    )) {
      return;
    }

    setSeatingSaving(true);
    try {
      const payload = { unitPrice: seatingForm.unitPrice };
      if (selectedOrder.ticket?.isHome) {
        payload.sector = seatingForm.sector;
      }

      const response = await api.put(`/ticket-orders/${selectedOrder._id}/seating`, payload);
      alert('Modifications enregistrées. Le membre a été notifié par e-mail.');
      setSelectedOrder(response.data.order);
      setOrders((prev) => prev.map((o) => (
        o._id === response.data.order._id ? response.data.order : o
      )));
    } catch (error) {
      console.error('Error updating order seating:', error);
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setSeatingSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'En attente', class: 'status-pending' },
      confirmed: { label: 'Confirmée', class: 'status-confirmed' },
      cancelled: { label: 'Annulée', class: 'status-cancelled' }
    };
    const statusInfo = statusMap[status] || { label: status, class: 'status-unknown' };
    return <span className={`status ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const matchOptions = useMemo(() => {
    const map = new Map();
    orders.forEach((o) => {
      const tid = o.ticket?._id;
      if (!tid) return;
      const id = String(tid);
      if (!map.has(id)) {
        map.set(id, {
          id,
          matchTitle: o.ticket?.matchTitle || '',
          date: o.ticket?.date,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const statusOk = filterStatus === 'all' || order.status === filterStatus;
      const ticketId = order.ticket?._id != null ? String(order.ticket._id) : '';
      const matchOk = filterMatch === 'all' || ticketId === filterMatch;
      return statusOk && matchOk;
    });
  }, [orders, filterStatus, filterMatch]);

  const handleExportExcel = () => {
    if (filteredOrders.length === 0) {
      alert('Aucune commande à exporter avec les filtres actuels.');
      return;
    }
    const includeTravel = filteredOrders.some((order) => orderHasTravelInformation(order));
    const exportHeaders = includeTravel
      ? [...EXCEL_HEADERS, ...EXCEL_TRAVEL_HEADERS]
      : EXCEL_HEADERS;
    const rows = filteredOrders.flatMap((order) => orderToExcelRows(order, { includeTravel }));
    const worksheet = XLSX.utils.aoa_to_sheet([exportHeaders, ...rows]);
    const columnWidths = exportHeaders.map((h, index) => {
      const headerLength = h.length;
      const maxData = Math.max(...rows.map((r) => String(r[index] ?? '').length), 8);
      return { wch: Math.min(Math.max(headerLength, maxData, 10), 60) };
    });
    worksheet['!cols'] = columnWidths;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Commandes');
    const day = new Date().toISOString().split('T')[0];
    const matchSuffix =
      filterMatch !== 'all'
        ? `_${matchOptions.find((m) => m.id === filterMatch)?.matchTitle?.slice(0, 40).replace(/[^\w\-]+/g, '_') || filterMatch}`
        : '';
    XLSX.writeFile(workbook, `commandes_billets${matchSuffix}_${day}.xlsx`);
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <h1>Commandes de Billets</h1>
          <div className="header-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <select
              value={filterMatch}
              onChange={(e) => setFilterMatch(e.target.value)}
              style={{ padding: '8px', fontSize: '14px', minWidth: '220px' }}
            >
              <option value="all">Tous les matchs</option>
              {matchOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.matchTitle || 'Match'}
                  {m.date ? ` — ${formatMatchDateOnly(m.date, 'fr-FR')}` : ''}
                </option>
              ))}
            </select>
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '8px', fontSize: '14px' }}
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="confirmed">Confirmées</option>
              <option value="cancelled">Annulées</option>
            </select>
            <button
              type="button"
              className="btn-submit"
              onClick={handleExportExcel}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <FaFileExcel /> Exporter Excel ({filteredOrders.length})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date de commande</th>
                  <th>Membre</th>
                  <th>Email</th>
                  <th>Match</th>
                  <th>Date du match</th>
                  <th>Quantité</th>
                  <th>e-card</th>
                  <th>Code PNR</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                      Aucune commande trouvée
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order._id}>
                      <td>{formatDate(order.orderDate)}</td>
                      <td>
                        {order.user 
                          ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() || 'N/A'
                          : 'N/A'}
                      </td>
                      <td>{order.user?.email || 'N/A'}</td>
                      <td>{order.ticket?.matchTitle || 'N/A'}</td>
                      <td>{order.ticket?.date ? formatMatchDate(order.ticket.date, 'fr-FR') : 'N/A'}</td>
                      <td>{order.quantity}</td>
                      <td>{getOrderEcardDisplay(order)}</td>
                      <td>{getOrderPnrDisplay(order)}</td>
                      <td>{getStatusBadge(order.status)}</td>
                      <td className="actions">
                        <button 
                          className="btn-view"
                          onClick={() => handleViewDetails(order)}
                          title="Voir détails"
                        >
                          <FaEye />
                        </button>
                        {order.status === 'pending' && (
                          <>
                            <button 
                              className="btn-edit"
                              onClick={() => handleStatusChange(order._id, 'confirmed')}
                              title="Confirmer"
                            >
                              <FaCheck />
                            </button>
                            <button 
                              className="btn-delete"
                              onClick={() => handleStatusChange(order._id, 'cancelled')}
                              title="Annuler"
                            >
                              <FaTimes />
                            </button>
                          </>
                        )}
                        <button 
                          className="btn-delete"
                          onClick={() => handleDelete(order._id)}
                          title="Supprimer"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedOrder && (
          <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Détails de la commande</h2>
                <button className="btn-close" onClick={() => setShowDetailModal(false)}>×</button>
              </div>
              
              <div className="subscriber-details">
                <section className="detail-section">
                  <h3>Informations de la commande</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Date de commande:</label>
                      <span>{formatDate(selectedOrder.orderDate)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Statut:</label>
                      <span>{getStatusBadge(selectedOrder.status)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Quantité:</label>
                      <span>{selectedOrder.quantity}</span>
                    </div>
                  </div>
                </section>

                <section className="detail-section">
                  <h3>Informations du membre</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Civilité:</label>
                      <span>{selectedOrder.user?.civilite || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Prénom:</label>
                      <span>{selectedOrder.user?.firstName || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Nom:</label>
                      <span>{selectedOrder.user?.lastName || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Sexe:</label>
                      <span>{selectedOrder.user?.sexe || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Date de naissance:</label>
                      <span>
                        {selectedOrder.user?.dateOfBirth
                          ? formatBirthDateDisplayUtc(selectedOrder.user.dateOfBirth, 'fr-CH') || 'N/A'
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Email:</label>
                      <span>{selectedOrder.user?.email || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Email secondaire:</label>
                      <span>{selectedOrder.user?.secondaryEmail || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Téléphone:</label>
                      <span>{formatPhoneDisplay(selectedOrder.user?.phone)}</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Lieu de naissance:</label>
                      <span>
                        {selectedOrder.user?.birthPlace 
                          ? `${selectedOrder.user.birthPlace.city || ''}${selectedOrder.user.birthPlace.canton ? ` (${selectedOrder.user.birthPlace.canton})` : ''}, ${selectedOrder.user.birthPlace.country || ''}`
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Adresse:</label>
                      <span>
                        {selectedOrder.user?.address 
                          ? `${selectedOrder.user.address.street || ''} ${selectedOrder.user.address.streetNumber || ''}, ${selectedOrder.user.address.postalCode || ''} ${selectedOrder.user.address.city || ''}${selectedOrder.user.address.canton ? ` (${selectedOrder.user.address.canton})` : ''}, ${selectedOrder.user.address.country || ''}`
                          : 'N/A'}
                      </span>
                    </div>
                    {selectedOrder.user?.codiceFiscale && (
                      <div className="detail-item">
                        <label>Codice Fiscale:</label>
                        <span>{selectedOrder.user.codiceFiscale}</span>
                      </div>
                    )}
                    {selectedOrder.user?.identity && (
                      <>
                        <div className="detail-item">
                          <label>Type de document:</label>
                          <span>{selectedOrder.user.identity.type || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Numéro de document:</label>
                          <span>{selectedOrder.user.identity.number || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Échéance document:</label>
                          <span>{selectedOrder.user.identity.expiryDate ? formatDateOnly(selectedOrder.user.identity.expiryDate) : 'N/A'}</span>
                        </div>
                      </>
                    )}
                    {selectedOrder.user?.juventusFanCard?.has && (
                      <>
                        <div className="detail-item">
                          <label>Tessera del Tifoso / Juventus Card:</label>
                          <span>Oui</span>
                        </div>
                        <div className="detail-item">
                          <label>Numéro TDT/Juventus Card:</label>
                          <span>{selectedOrder.user.juventusFanCard.number || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Échéance TDT:</label>
                          <span>{selectedOrder.user.juventusFanCard.expiryDate ? formatDateOnly(selectedOrder.user.juventusFanCard.expiryDate) : 'N/A'}</span>
                        </div>
                        {selectedOrder.user.juventusFanCard.webticketPIN && (
                          <div className="detail-item">
                            <label>Webticket PIN:</label>
                            <span>{selectedOrder.user.juventusFanCard.webticketPIN}</span>
                          </div>
                        )}
                      </>
                    )}
                    {selectedOrder.user?.subscription?.has && (
                      <>
                        <div className="detail-item">
                          <label>Abonnement:</label>
                          <span>Oui</span>
                        </div>
                        <div className="detail-item">
                          <label>Tribune:</label>
                          <span>{selectedOrder.user.subscription.tribune || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Settore:</label>
                          <span>{selectedOrder.user.subscription.settore || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Fila:</label>
                          <span>{selectedOrder.user.subscription.fila || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Posto:</label>
                          <span>{selectedOrder.user.subscription.posto || 'N/A'}</span>
                        </div>
                      </>
                    )}
                    {selectedOrder.user?.blackWhiteCard?.has && (
                      <>
                        <div className="detail-item">
                          <label>Carte Black & White / J1897:</label>
                          <span>Oui</span>
                        </div>
                        <div className="detail-item">
                          <label>Numéro B&W/J1897:</label>
                          <span>{selectedOrder.user.blackWhiteCard.number || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Échéance B&W:</label>
                          <span>{selectedOrder.user.blackWhiteCard.expiryDate ? formatDateOnly(selectedOrder.user.blackWhiteCard.expiryDate) : 'N/A'}</span>
                        </div>
                      </>
                    )}
                    {selectedOrder.user?.isMinor && selectedOrder.user?.parentInfo && (
                      <>
                        <div className="detail-item full-width">
                          <label style={{ fontWeight: 700, marginTop: '1rem' }}>Informations du parent (mineur):</label>
                        </div>
                        <div className="detail-item">
                          <label>Prénom parent:</label>
                          <span>{selectedOrder.user.parentInfo.firstName || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Nom parent:</label>
                          <span>{selectedOrder.user.parentInfo.lastName || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Email parent:</label>
                          <span>{selectedOrder.user.parentInfo.email || 'N/A'}</span>
                        </div>
                        {selectedOrder.user.parentInfo.sexe && (
                          <div className="detail-item">
                            <label>Sexe parent:</label>
                            <span>{selectedOrder.user.parentInfo.sexe}</span>
                          </div>
                        )}
                        {selectedOrder.user.parentInfo.dateOfBirth && (
                          <div className="detail-item">
                            <label>Date de naissance parent:</label>
                            <span>
                              {selectedOrder.user.parentInfo.dateOfBirth
                                ? formatBirthDateDisplayUtc(
                                    selectedOrder.user.parentInfo.dateOfBirth,
                                    'fr-CH',
                                  ) || 'N/A'
                                : 'N/A'}
                            </span>
                          </div>
                        )}
                        {selectedOrder.user.parentInfo.birthCity && (
                          <div className="detail-item">
                            <label>Ville de naissance parent:</label>
                            <span>{selectedOrder.user.parentInfo.birthCity}</span>
                          </div>
                        )}
                      </>
                    )}
                    <div className="detail-item">
                      <label>Langue préférée:</label>
                      <span>{selectedOrder.user?.preferredLanguage === 'fr' ? 'Français' : selectedOrder.user?.preferredLanguage === 'it' ? 'Italien' : 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Statut membre:</label>
                      <span>{selectedOrder.user?.isMember ? 'Membre actif' : 'Non membre'}</span>
                    </div>
                    {selectedOrder.user?.ticketCredit !== undefined && (
                      <div className="detail-item">
                        <label>Crédit billets:</label>
                        <span>{selectedOrder.user.ticketCredit || 0}</span>
                      </div>
                    )}
                  </div>
                </section>

                <section className="detail-section">
                  <h3>Informations du billet</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Match:</label>
                      <span>{selectedOrder.ticket?.matchTitle || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Stade:</label>
                      <span>{selectedOrder.ticket?.stadium || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Date du match:</label>
                      <span>{selectedOrder.ticket?.date ? formatMatchDate(selectedOrder.ticket.date, 'fr-FR') : 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Zone:</label>
                      <span>{selectedOrder.area || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Secteur:</label>
                      <span>{formatSectorDisplay(selectedOrder.sector)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Quantité:</label>
                      <span>{selectedOrder.quantity || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>e-card:</label>
                      <span>{getOrderEcardDisplay(selectedOrder)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Code PNR:</label>
                      <span>{getOrderPnrDisplay(selectedOrder)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Prix unitaire:</label>
                      <span>
                        {formatOrderUnitPriceDisplay(selectedOrder.ticket, selectedOrder)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Prix total:</label>
                      <span>
                        {(() => {
                          const total = getOrderTotalPriceFromOrder(
                            selectedOrder.ticket,
                            selectedOrder,
                          );
                          return total != null ? `${total.toFixed(2)} €` : 'N/A';
                        })()}
                      </span>
                    </div>
                    {selectedOrder.paymentMethod && (
                      <div className="detail-item">
                        <label>Mode de repaiement:</label>
                        <span>
                          {selectedOrder.paymentMethod}
                          {selectedOrder.paymentCurrency ? ` (${selectedOrder.paymentCurrency})` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </section>

                {getOrderAttendees(selectedOrder).length > 0 && (
                  <section className="detail-section">
                    <h3>Titulaires des billets</h3>
                    <div className="detail-grid">
                      {renderOrderAttendeesDetail(selectedOrder)}
                    </div>
                  </section>
                )}

                {renderOrderTravelInformationSection(selectedOrder)}

                {selectedOrder.status !== 'cancelled' && (
                  <section className="detail-section">
                    <h3><FaEdit style={{ marginRight: '8px' }} />Modifier secteur et prix</h3>
                    <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.95rem' }}>
                      En cas de rupture de places dans le secteur commandé, modifiez l&apos;attribution
                      et le prix. Le membre recevra un e-mail automatique à la validation.
                    </p>
                    <div className="detail-grid">
                      {selectedOrder.ticket?.isHome && (
                        <div className="detail-item full-width">
                          <label htmlFor="seating-sector">Nouveau secteur:</label>
                          <select
                            id="seating-sector"
                            value={seatingForm.sector}
                            onChange={(e) => handleSeatingSectorChange(e.target.value)}
                            style={{ padding: '8px', fontSize: '14px', width: '100%', maxWidth: '480px' }}
                          >
                            <option value="">— Sélectionner un secteur —</option>
                            {sectorOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="detail-item">
                        <label htmlFor="seating-unit-price">Prix unitaire (€):</label>
                        <input
                          id="seating-unit-price"
                          type="number"
                          min="0"
                          step="0.01"
                          value={seatingForm.unitPrice}
                          onChange={(e) => setSeatingForm((prev) => ({
                            ...prev,
                            unitPrice: e.target.value,
                          }))}
                          style={{ padding: '8px', fontSize: '14px', width: '100%', maxWidth: '200px' }}
                        />
                      </div>
                      <div className="detail-item">
                        <label>Nouveau prix total:</label>
                        <span>
                          {seatingForm.unitPrice !== '' && !Number.isNaN(Number(seatingForm.unitPrice))
                            ? `${(Number(seatingForm.unitPrice) * (selectedOrder.quantity || 1)).toFixed(2)} €`
                            : '—'}
                        </span>
                      </div>
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="btn-submit"
                        onClick={handleSeatingSave}
                        disabled={seatingSaving || (
                          selectedOrder.ticket?.isHome && !seatingForm.sector
                        ) || seatingForm.unitPrice === ''}
                      >
                        {seatingSaving ? 'Enregistrement…' : 'Valider et notifier le membre'}
                      </button>
                    </div>
                  </section>
                )}

                {selectedOrder.notes && (
                  <section className="detail-section">
                    <h3>Notes</h3>
                    <div className="detail-grid">
                      <div className="detail-item full-width">
                        <p>{selectedOrder.notes}</p>
                      </div>
                    </div>
                  </section>
                )}
              </div>

              <div className="modal-footer">
                {selectedOrder.status === 'pending' && (
                  <>
                    <button 
                      className="btn-submit"
                      onClick={() => {
                        handleStatusChange(selectedOrder._id, 'confirmed');
                        setShowDetailModal(false);
                      }}
                    >
                      <FaCheck /> Confirmer la commande
                    </button>
                    <button 
                      className="btn-cancel"
                      onClick={() => {
                        handleStatusChange(selectedOrder._id, 'cancelled');
                        setShowDetailModal(false);
                      }}
                    >
                      <FaTimes /> Annuler la commande
                    </button>
                  </>
                )}
                <button 
                  className="btn-cancel"
                  onClick={() => setShowDetailModal(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTicketOrders;

